import { useMemo } from 'react'
import { useAppSelector } from '@/store/hooks'
import type { WorkspaceResponse, WorkspaceAISettingsSchema } from '@/api'

/**
 * 정규화된 워크스페이스 AI 기능 설정.
 * 생성 타입(WorkspaceAISettingsSchema)은 필드가 모두 optional이라,
 * 사용처에서는 누락을 비활성으로 정규화한 이 형태를 쓴다.
 */
export interface WorkspaceAiSettings {
  enabled: boolean
  allowed_roles: string[]
}

const DISABLED_AI_SETTINGS: WorkspaceAiSettings = { enabled: false, allowed_roles: [] }

/**
 * ai_settings 누락 대비 정규화 — 구버전 백엔드 응답이나 localStorage에 캐시된
 * 과거 워크스페이스 객체에는 필드가 없을 수 있으므로, 없으면 비활성으로 간주한다.
 */
export const getWorkspaceAiSettings = (
  workspace: WorkspaceResponse | null | undefined
): WorkspaceAiSettings => {
  const settings: WorkspaceAISettingsSchema | undefined = workspace?.ai_settings
  if (!settings) return DISABLED_AI_SETTINGS
  return {
    enabled: settings.enabled === true,
    allowed_roles: Array.isArray(settings.allowed_roles) ? settings.allowed_roles : [],
  }
}

/**
 * 현재 선택된 워크스페이스에서 AI 기능 UI를 노출할지 판정.
 *
 *   canUseAI = ai_settings.enabled && ai_settings.allowed_roles.includes(내 역할)
 *
 * - 내 역할은 현재 기관 멤버의 role(owner/admin/normal). 워크스페이스 전환 시
 *   selectWorkspaceAndFetchOrgMember 가 selectedWorkspace 와 orgMember 를 모두
 *   갱신하므로 이 훅은 자동으로 재판정된다.
 * - 설정 변경은 Django admin 전용 (프론트에 수정 UI 없음).
 * - UI 노출 제어일 뿐 보안 경계가 아님 — 실제 권한 검사는 서버가 담당한다.
 */
export const useCanUseAI = (): boolean => {
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  const orgMember = useAppSelector(state => state.auth.orgMember)

  return useMemo(() => {
    const aiSettings = getWorkspaceAiSettings(selectedWorkspace)
    const myRole = orgMember?.role
    return aiSettings.enabled && !!myRole && aiSettings.allowed_roles.includes(myRole)
  }, [selectedWorkspace, orgMember])
}
